import { FC } from 'react';
import { css } from '@emotion/react';
import { TableInstance } from 'react-table';
import Indicator, { Rating } from '../RatingIndicator';
import { fontStyles } from '../../styles';
import formatDisplay, { FormatterStyle } from '../../utils/formatDisplay';
import { toTitleCase } from '../../utils/symbolUtils';
import PriceRange from '../PriceRange';
import ColoredPercentChange from '../ColoredPercentChange';
import AllocationBar from '../AllocationBar';
import { BasicInvestment } from '../../apis/backend';

import { BREAKPOINTS } from '../../constants/ui';
import { ColumnId } from './columns';

interface TableRowData extends Partial<BasicInvestment> {
  allocation_percentage?: number;
}

// Exported for testing - these column IDs render with ColoredPercentChange
export const percentChangeColumns = [
  ColumnId.ChangePercent1d,
  ColumnId.ChangePercent1w,
  ColumnId.ChangePercent1m,
  ColumnId.ChangePercent3m,
  ColumnId.ChangePercent6m,
  ColumnId.ChangePercentYtd,
  ColumnId.ChangePercent1y,
  ColumnId.ChangePercent5y,
  ColumnId.RevenueGrowth1y,
];

const percentColumns = [
  ColumnId.PercentMarketCapTraded1d,
  ColumnId.CashflowYield,
  ColumnId.CapexToRevenue,
  ColumnId.InsidersPercentTraded3m,
];

interface Props extends TableInstance {
  emptyText: string;
  row?: {
    original: TableRowData;
  };
  realTimePrices?: Record<
    string,
    {
      last?: number;
      bid?: number;
      ask?: number;
      changePercent1d?: number;
      changePercent1w?: number;
      changePercent1m?: number;
      changePercent3m?: number;
      changePercent6m?: number;
      changePercentYtd?: number;
      changePercent1y?: number;
      changePercent5y?: number;
    }
  >;
}

const Cell: FC<Props> = ({ column, value, emptyText = '', row, realTimePrices }) => {
  const styles = {
    indicator: css`
      &:not(:first-of-type) {
        margin-left: 1px;
      }

      @media (max-width: ${BREAKPOINTS.MOBILE_MAX}px) {
        transform: scale(0.85);
        transform-origin: left center;
      }
    `,
    name: css`
      ${fontStyles.flea}

      @media (max-width: ${BREAKPOINTS.SMALL_PHONE}px) {
        font-size: 0.75rem;
      }

      @media (max-width: ${BREAKPOINTS.MOBILE_MAX}px) {
        font-size: 0.7rem;
      }
    `,
    gray_percent: css`
      color: inherit;

      @media (max-width: ${BREAKPOINTS.MOBILE_MAX}px) {
        font-size: 0.85rem;
      }
    `,
    priceRange: css`
      width: 82%;
      display: inline-block;

      @media (max-width: ${BREAKPOINTS.MOBILE_MAX}px) {
        width: 100%;
      }
    `,
    secondary: css`
      color: #999;
      ${fontStyles.flea}
    `,
  };

  if (!value) return <span>{emptyText}</span>;

  if (column.id === ColumnId.BottomLine) {
    const isStock = 'profitRating' in value && value.profitRating != null && value.profitRating != 'MISSING';

    const headings = isStock ? ['profits', 'growth', 'price', 'insider'] : ['return', 'risk', 'size', 'fees'];

    const ratings = isStock
      ? [value.profitRating, value.growthRating, value.priceRating, value.insiderRating]
      : [value.returnRating, value.riskRating, value.sizeRating, value.feesRating];

    return (
      <span>
        {ratings.map((rating: Rating, index: number) => (
          <span css={styles.indicator} key={index}>
            <Indicator heading={headings[index]} rating={rating} />
          </span>
        ))}
      </span>
    );
  }

  if (column.id === ColumnId.Week52Range) {
    if (!value.high || !value.low || !value.current) return <span />;
    return (
      <div css={styles.priceRange}>
        <PriceRange
          highPrice={value.high}
          lowPrice={value.low}
          markedPrice={value.current}
          currency={row?.original?.currency}
        />
      </div>
    );
  }

  if (percentChangeColumns.includes(column.id)) {
    // Map column IDs to real-time change percent fields
    const symbol = row?.original?.symbol;
    const symbolPrices = symbol ? realTimePrices?.[symbol] : undefined;

    let realTimeChangePercent: number | undefined = undefined;

    if (symbolPrices) {
      switch (column.id) {
        case ColumnId.ChangePercent1d:
          realTimeChangePercent = symbolPrices.changePercent1d;
          break;
        case ColumnId.ChangePercent1w:
          realTimeChangePercent = symbolPrices.changePercent1w;
          break;
        case ColumnId.ChangePercent1m:
          realTimeChangePercent = symbolPrices.changePercent1m;
          break;
        case ColumnId.ChangePercent3m:
          realTimeChangePercent = symbolPrices.changePercent3m;
          break;
        case ColumnId.ChangePercent6m:
          realTimeChangePercent = symbolPrices.changePercent6m;
          break;
        case ColumnId.ChangePercentYtd:
          realTimeChangePercent = symbolPrices.changePercentYtd;
          break;
        case ColumnId.ChangePercent1y:
          realTimeChangePercent = symbolPrices.changePercent1y;
          break;
        case ColumnId.ChangePercent5y:
          realTimeChangePercent = symbolPrices.changePercent5y;
          break;
      }
    }

    const displayValue = realTimeChangePercent !== undefined ? realTimeChangePercent : value;
    const useAnimation = realTimeChangePercent !== undefined;

    return <ColoredPercentChange value={displayValue} animated={useAnimation} />;
  }

  if (percentColumns.includes(column.id)) {
    return <span css={styles.gray_percent}>{formatDisplay(FormatterStyle.percentDetailed, value)}</span>;
  }

  if (column.id === ColumnId.Name) {
    const allocation_percentage = row?.original?.allocation_percentage;
    const displayName = typeof value === 'string' ? toTitleCase(value) : value;

    if (
      allocation_percentage !== undefined &&
      allocation_percentage !== null &&
      !isNaN(allocation_percentage) &&
      allocation_percentage >= 0
    ) {
      return (
        <div>
          <span css={styles.name}>{displayName}</span>
          <AllocationBar percentage={allocation_percentage} />
        </div>
      );
    }

    return <span css={styles.name}>{displayName}</span>;
  }

  if (column.id === ColumnId.Symbol) {
    const allocation_percentage = row?.original?.allocation_percentage;

    if (
      allocation_percentage !== undefined &&
      allocation_percentage !== null &&
      !isNaN(allocation_percentage) &&
      allocation_percentage >= 0
    ) {
      return (
        <div>
          <span css={styles.name}>{value}</span>
          <AllocationBar percentage={allocation_percentage} />
        </div>
      );
    }

    return <span css={styles.name}>{value}</span>;
  }

  if (column.id === ColumnId.LatestPrice) {
    const symbol = row?.original?.symbol;
    const realTimePrice = symbol && realTimePrices?.[symbol]?.last;
    const displayPrice = realTimePrice ?? value;

    if (displayPrice !== undefined && displayPrice !== null) {
      return <span>{formatDisplay(FormatterStyle.moneySmall, displayPrice, row?.original?.currency)}</span>;
    }

    return <span>{emptyText}</span>;
  }

  if (column.id === ColumnId.MarketCap) {
    return <span>{formatDisplay(FormatterStyle.moneyBig, value, row?.original?.currency)}</span>;
  }

  // Plaid-specific columns
  if (column.id === ColumnId.CurrentValue) {
    return <span css={styles.name}>{formatDisplay(FormatterStyle.moneyCompact, value)}</span>;
  }

  if (column.id === ColumnId.CostBasis) {
    if (value === undefined || value === null) return <span css={styles.secondary}>--</span>;
    return <span css={styles.name}>{formatDisplay(FormatterStyle.moneyCompact, value)}</span>;
  }

  if (column.id === ColumnId.ReturnAmount) {
    if (value === undefined || value === null) return <span css={styles.secondary}>--</span>;

    // Show return amount with sign and compact format
    // Use amount for display but normalize to ratio for color calculation
    const amount = value as number;
    const sign = amount >= 0 ? '+' : '';
    const formattedAmount = formatDisplay(FormatterStyle.moneyCompact, Math.abs(amount));
    const displayText = `${sign}${amount < 0 ? '-' : ''}${formattedAmount}`;

    // Pass a normalized ratio (0.01 = 1%) for color calculation based on sign
    // We use a fixed value since dollar amounts don't map to percentage ratios
    const colorValue = amount >= 0 ? 0.01 : -0.01;
    return <ColoredPercentChange value={colorValue} customDisplay={displayText} />;
  }

  if (column.id === ColumnId.ReturnPercent) {
    if (value === undefined || value === null) return <span css={styles.secondary}>--</span>;

    // Backend returns percentage as whole number (50 = 50%), but calculateChangeColor expects ratio (0.50 = 50%)
    const percent = value as number;
    const ratio = percent / 100;
    const formattedPercent = formatDisplay(FormatterStyle.percentChangeCompact, percent);
    return <ColoredPercentChange value={ratio} customDisplay={formattedPercent} />;
  }

  return <span>{value}</span>;
};

export default Cell;
